/**
 * Centralized logout for an invalidated token.
 *
 * Core (CheckJWT) invalidates the previous JWT when the same account signs in on
 * another browser; from then on every request from the old browser answers 401/403
 * with `data.clean = true`. This module turns that response into a controlled
 * logout: it clears the local session, shows a single "session ended" notice on
 * the login screen and redirects, without retrying or stacking technical errors.
 */
import Cookies from 'js-cookie';
import { destroyCookies } from './sessionCookies';

// Prevents parallel failed requests from triggering several notices / redirects.
// The first one wins; the rest do nothing until the reload.
let handling = false;

// Flags an intentional logout so the "signed in on another device" notice is not
// shown when the user clicked the logout button themselves.
let loggingOut = false;

export const SESSION_ENDED_NOTICE_KEY = 'sessionEndedNotice';
export const LOGOUT_NOTICE_KEY = 'logoutNotice';

/**
 * Tells whether a response means the session was invalidated by core.
 *
 * Only `data.clean === true` is reliable: a 401/403 without it can be wrong credentials,
 * a forbidden resource or a failed reCAPTCHA, and must not end the session.
 *
 * @param {{clean?: boolean}} [body] `data` body of the response.
 */
export const isSessionInvalidResponse = (body) => Boolean(body && body.clean === true);

/**
 * Must be called at the start of an intentional logout to suppress the
 * "another device" notice in the invalid-token logout flow.
 */
export const beginIntentionalLogout = () => {
  loggingOut = true;
};

/**
 * Controlled logout for an invalid token. Idempotent: only the first call has an
 * effect. Clears the local session, leaves a flash notice for the login screen
 * and reloads at the root.
 */
export const handleSessionExpired = () => {
  if (handling) return;

  // A visitor who never signed in has nothing to invalidate: no reload and no notice.
  if (!Cookies.get('authentication_token_02')) {
    destroyCookies();
    return;
  }

  handling = true;

  localStorage.removeItem('tokenExpirationDateStored');
  destroyCookies();

  if (!loggingOut) {
    try {
      sessionStorage.setItem(
        SESSION_ENDED_NOTICE_KEY,
        'Tu sesión finalizó porque se inició sesión con esta cuenta en otro dispositivo.'
      );
    } catch (e) {
      // sessionStorage may be unavailable; the redirect still goes ahead.
    }
  }

  // Full reload: aborts any in-flight request and clears React state so no
  // errors reappear after the redirect.
  window.location.assign('/');
};
